import L from "leaflet";

// ===============================
// CONSTANTS
// ===============================

// Base layers configuration
export const BASE_LAYERS = [
  {
    name: "CartoDB Positron",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
    attribution: "© CartoDB",
  },
  {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap contributors",
  },
  {
    name: "Satellite (Esri)",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
  /* {
    name: "CartoDB Dark",
    url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
    attribution: "© CartoDB",
  }, */
];

const LEAFLET_CDN_URLS = {
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
};

const STATUS_COLORS = {
  completed: "#22c55e",
  "in progress": "#3b82f6",
  "on hold": "#f59e0b",
  cancelled: "#ef4444",
  default: "#6b7280",
};

const MARKER_SIZES = {
  project: { width: 30, height: 30, iconSize: [26, 26], iconAnchor: [13, 13] },
  excavation: { width: 18, height: 18, iconSize: [18, 18], iconAnchor: [9, 9] },
};

// ===============================
// LEAFLET CONFIGURATION
// ===============================

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions(LEAFLET_CDN_URLS);

// ===============================
// ICON GENERATORS
// ===============================

/**
 * Creates a custom project marker icon based on status
 */
const createProjectIconHTML = (color, status) => `
  <div style="
    width: ${MARKER_SIZES.project.width}px;
    height: ${MARKER_SIZES.project.height}px;
    background-color: ${color};
    border: 3px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 12px rgba(0,0,0,0.3);
    position: relative;
  ">
    <div style="
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 12px;
      height: 12px;
      background-color: #ffffff;
      border-radius: 50%;
    "></div>
  </div>
`;

// Project icon configurations (Circle markers untuk membedakan dari excavation)
const PROJECT_ICONS = Object.fromEntries(
  Object.entries(STATUS_COLORS).map(([status, color]) => [
    status,
    new L.DivIcon({
      className: "custom-project-marker",
      html: createProjectIconHTML(color, status),
      iconSize: MARKER_SIZES.project.iconSize,
      iconAnchor: MARKER_SIZES.project.iconAnchor,
      popupAnchor: [0, -13],
    }),
  ])
);

// ===============================
// PUBLIC FUNCTIONS
// ===============================

/**
 * Creates a custom project marker icon based on status
 * @param {string} status - Project status
 * @returns {L.DivIcon} Leaflet DivIcon
 */
export const createCustomIcon = (status) => {
  const normalizedStatus = status?.toLowerCase() || "default";
  return PROJECT_ICONS[normalizedStatus] || PROJECT_ICONS.default;
};

/**
 * Validates and normalizes coordinate data
 * @param {Object} coordinates - Coordinate object with coordinates array
 * @returns {Object|null} Normalized {lat, lng} object or null if invalid
 */
export const validateCoordinates = (coordinates) => {
  // Check if coordinates object and array exist
  if (!coordinates?.coordinates || !Array.isArray(coordinates.coordinates)) {
    return null;
  }

  const coords = coordinates.coordinates;

  // Validate array length and types
  if (
    coords.length < 2 ||
    typeof coords[0] !== "number" ||
    typeof coords[1] !== "number"
  ) {
    return null;
  }

  // Extract coordinates (assume [lng, lat] format)
  let lat = coords[1];
  let lng = coords[0];

  // Validate coordinate ranges and swap if needed
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    if (Math.abs(coords[0]) <= 90 && Math.abs(coords[1]) <= 180) {
      // Swap coordinates if they're in [lat, lng] format
      lat = coords[0];
      lng = coords[1];
    } else {
      return null;
    }
  }

  return { lat, lng };
};

// Excavation status colors
const EXCAVATION_STATUS_COLORS = {
  active: "#22c55e", // green
  planned: "#eab308", // yellow
  completed: "#3b82f6", // blue
  suspended: "#ef4444", // red
  inactive: "#9ca3af", // gray
  "in-progress": "#3b82f6", // blue
  default: "#9ca3af", // gray
};

/**
 * Creates an excavation point marker icon
 * @param {string} status - Excavation status
 * @returns {L.DivIcon} Leaflet DivIcon for excavation points
 */
export const createExcavationIcon = (status = "default") => {
  const color =
    EXCAVATION_STATUS_COLORS[status?.toLowerCase()] ||
    EXCAVATION_STATUS_COLORS.default;
  return L.divIcon({
    html: `<div style="
      background: ${color};
      width: ${MARKER_SIZES.excavation.width}px;
      height: ${MARKER_SIZES.excavation.height}px;
      border-radius: 50%;
      border: 2px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
      transform: translate(-50%, -50%);
    "></div>`,
    className: "excavation-marker-icon",
    iconSize: MARKER_SIZES.excavation.iconSize,
    iconAnchor: MARKER_SIZES.excavation.iconAnchor,
    popupAnchor: [0, -9],
  });
};

// Get status color classes
export const getStatusColor = (status) => {
  return STATUS_COLORS[status?.toLowerCase()] || STATUS_COLORS.inactive;
};

// Format date helper
export const formatDate = (dateString) => {
  if (!dateString) return "Not set";

  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid date";
  }
};

// Get unique status options from data
export const getUniqueStatuses = (data, field = "status") => {
  const statuses = data
    .map((item) => item[field])
    .filter((status) => status && status.trim() !== "")
    .map((status) => status.toLowerCase());

  return [...new Set(statuses)].sort();
};

// Filter data based on search term and status
export const filterData = (
  data,
  searchTerm,
  statusFilter,
  searchFields = ["name"]
) => {
  return data.filter((item) => {
    // Status filter
    const matchesStatus =
      statusFilter === "all" ||
      item.status?.toLowerCase() === statusFilter.toLowerCase();

    // Search filter
    const matchesSearch =
      searchTerm === "" ||
      searchFields.some((field) => {
        const value = getNestedValue(item, field);
        return value && value.toLowerCase().includes(searchTerm.toLowerCase());
      });

    return matchesStatus && matchesSearch;
  });
};

// Get nested object value by dot notation
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

// Debounce function for search input
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Format number with thousand separators
export const formatNumber = (num) => {
  return new Intl.NumberFormat("id-ID").format(num);
};

// Get status display name
export const getStatusDisplayName = (status) => {
  const statusNames = {
    active: "Aktif",
    inactive: "Tidak Aktif",
    pending: "Menunggu",
    completed: "Selesai",
    cancelled: "Dibatalkan",
    "in-progress": "Sedang Berlangsung",
    planned: "Direncanakan",
    delayed: "Tertunda",
    suspended: "Ditangguhkan",
  };

  return statusNames[status?.toLowerCase()] || status || "Tidak Diketahui";
};
